import { useEffect } from "react";
import {
    IInputProps,
    useRhfFieldControlled
} from "../../helpers/reactHookFormsHelper";
import { useFormContext } from "react-hook-form";
import { useAuthState } from "_ReactContext/AuthContext";
import { useApi } from "customHooks/useApi";
import ComboAutoComplete from "../ComboAutoComplete";
import { Alert } from "@material-ui/lab";

export interface ISelectProps {
    customLists?: Compleo.IObject[];
    language?: string;
    disableClearable?: boolean;
    campoMetadata: Compleo.IObject;
    ignoreCurrentValueIfNotInList?: boolean;
    label?: string;
    completeMetadata?: Compleo.IObject[];
}

const JobApprovalField = (props: ISelectProps & IInputProps) => {
    const {
        name,
        helperTextDefault,
        t,
        campoMetadata,
        classes,
        language
    } = props;
    const rejectEmptyObject =
        campoMetadata?.elementConfig?.rejectEmptyObject === true;
    const { watch } = useRhfFieldControlled(name, helperTextDefault);
    const { company } = useAuthState();
    const { setValue: setValueRHF } = useFormContext();
    const customer = watch("customer");
    const branch = watch("branch");
    const category = watch("category");
    const hiddenFieldName = `${name}.availableJobApprovalList`;
    const { field: hiddenField } = useRhfFieldControlled(
        hiddenFieldName,
        helperTextDefault
    );

    const selectedFieldName = `${name}.selectedJobApproval`;
    const { field: selectedField } = useRhfFieldControlled(
        selectedFieldName,
        helperTextDefault
    );

    const postAddressSearch = `/jobapprovalworkflow/get-workflow-by-job-data`;
    const [getWorkflowSearchResult, updateResult] = useApi(
        postAddressSearch,
        "post",
        { companyId: company.companyId, branch, customer, category },
        false
    );
    const totalAvailable: number = (
        getWorkflowSearchResult?.response?.data?.items || []
    ).length;

    useEffect(() => {
        if (getWorkflowSearchResult.status === "success") {
            const data = getWorkflowSearchResult.response.data;
            setValueRHF(hiddenFieldName, data?.items || []);
            if (data?.items?.length === 1) {
                setValueRHF(selectedFieldName, data.items[0]);
            } else if (data?.items?.length > 1) {
                if (
                    !selectedField?.value?.value ||
                    (data?.items || []).filter(
                        (item: Compleo.IObject) =>
                            item.value === selectedField.value.value
                    ).length === 0
                ) {
                    setValueRHF(selectedFieldName, null);
                }
            } else {
                if (rejectEmptyObject) {
                    setValueRHF(selectedFieldName, null);
                } else {
                    setValueRHF(selectedFieldName, {});
                }
            }
        }
    }, [getWorkflowSearchResult.status, totalAvailable]);

    useEffect(() => {
        updateResult();
    }, [customer?.value, branch?.value, category?.value]);

    if (totalAvailable === 0 && !rejectEmptyObject) {
        return null;
    }

    return (
        <>
            <input
                type="hidden"
                name={hiddenFieldName}
                value={hiddenField.value}
            />
            {totalAvailable === 1 && (
                <>
                    <Alert severity="info">
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t("jobApprovalSettingsInfo", {
                                    jobApprovalName: selectedField.value?.label
                                })
                            }}
                        />
                    </Alert>
                    <input
                        type="hidden"
                        name={selectedFieldName}
                        value={selectedField.value || {}}
                    />
                </>
            )}
            {totalAvailable === 0 && rejectEmptyObject && (
                <Alert severity="error">
                    {t("jobApprovalSettingsNoAvailable")}
                </Alert>
            )}
            {totalAvailable > 1 && (
                <>
                    <Alert severity="info" style={{ marginBottom: "10px" }}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t("jobApprovalSettingsMoreThanOne")
                            }}
                        />
                    </Alert>
                    <ComboAutoComplete
                        required={true}
                        name={selectedFieldName}
                        label={t(name)}
                        options={hiddenField.value}
                        className={classes?.textField}
                        helperTextDefault={t(
                            `${selectedFieldName}_helperText`,
                            ""
                        )}
                        campoMetadata={campoMetadata}
                        language={language}
                    />
                </>
            )}
        </>
    );
};

export default JobApprovalField;
